import { AGENT_QUERIES_BASE_KEY } from '@/api/agents/queries/get-agent-query'
import { IApi } from '@/setup/api-client'
import { useInfiniteApiQuery } from '@/utils/api/api-infinite-query'
import { useApiToolbox } from '@/utils/api/use-api-toolbox'
import type { IAgentResponse, AgentStatus } from '@/features/agents/types/agent-types'

// Meta may be undefined when the frontend deploys before the backend
// (old backend returns { data: [...] } without meta)
export type IListAgentsMeta = { page: number; limit: number } | undefined

export const AGENTS_PAGE_SIZE = 30

export interface IGetAgentsQueryParams {
    organizationId: string | null
    brandSlug: string
    productUuid?: string
    page?: number
    limit?: number
    search?: string
    status?: AgentStatus
    archived?: boolean
    createdByClerkIds?: string[]
    projectUuid?: string
    pinned?: boolean
    includeManagerSystem?: boolean
}

export class GetAgentsQuery {
    static GET_AGENTS_QUERY_BASE_KEY = [...AGENT_QUERIES_BASE_KEY, 'get-agents-query']

    static getKey(params: Omit<IGetAgentsQueryParams, 'page'>) {
        return [
            ...GetAgentsQuery.GET_AGENTS_QUERY_BASE_KEY,
            params.organizationId,
            params.brandSlug,
            'get-agents',
            params.productUuid,
            params.search,
            params.status,
            params.archived,
            params.limit,
            params.createdByClerkIds,
            params.projectUuid,
            params.pinned,
            params.includeManagerSystem,
        ]
    }

    static async call(api: IApi, params: IGetAgentsQueryParams) {
        const queryParams: Record<string, string | string[]> = {}
        if (params.page !== undefined) queryParams.page = String(params.page)
        if (params.limit !== undefined) queryParams.limit = String(params.limit)
        if (params.search?.trim()) queryParams.search = params.search.trim()
        if (params.status) queryParams.status = params.status
        if (params.archived) queryParams.archived = 'true'
        if (params.createdByClerkIds?.length) queryParams.createdByClerkIds = params.createdByClerkIds
        if (params.projectUuid) queryParams.projectUuid = params.projectUuid
        if (params.pinned === true) queryParams.pinned = 'true'
        if (params.productUuid) queryParams.productUuid = params.productUuid
        if (params.includeManagerSystem === true) queryParams.includeManagerSystem = 'true'

        const url = `/organization/${params.organizationId}/brands/${params.brandSlug}/agents`
        return api.get<IAgentResponse[], IListAgentsMeta>(url, queryParams)
    }
}

export function useGetInfiniteAgentsQuery(options?: {
    search?: string
    status?: AgentStatus
    archived?: boolean
    createdByClerkIds?: string[]
    projectUuid?: string
    productUuid?: string
    pinned?: boolean
    includeManagerSystem?: boolean
    enabled?: boolean
    refetchInterval?: number | false
    structuralSharing?: boolean
}) {
    const { api, apiParams } = useApiToolbox()
    const organizationId = apiParams.organizationId ?? ''

    const params: Omit<IGetAgentsQueryParams, 'page'> = {
        organizationId: organizationId || null,
        brandSlug: apiParams.brandSlug,
        productUuid: options?.productUuid,
        limit: AGENTS_PAGE_SIZE,
        search: options?.search,
        status: options?.status,
        archived: options?.archived,
        createdByClerkIds: options?.createdByClerkIds,
        projectUuid: options?.projectUuid,
        pinned: options?.pinned,
        includeManagerSystem: options?.includeManagerSystem,
    }

    return useInfiniteApiQuery({
        queryKey: GetAgentsQuery.getKey(params),
        queryFn: ({ pageParam = 1 }) => GetAgentsQuery.call(api, { ...params, page: pageParam }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            if (!lastPage?.data || lastPage.data.length === 0) return null
            if (!lastPage.meta) return null
            if (lastPage.data.length < AGENTS_PAGE_SIZE) return null
            return lastPage.meta.page + 1
        },
        enabled: !!organizationId && !!apiParams.brandSlug && (options?.enabled ?? true),
        refetchInterval: options?.refetchInterval,
        structuralSharing: options?.structuralSharing ?? false,
    })
}

export type IGetInfiniteAgentsQueryData = ReturnType<typeof useGetInfiniteAgentsQuery>['data']
